import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  isPostHogFeatureEnabled,
  onPostHogFeatureFlags,
  onPostHogFeatureFlagsReloading,
  reloadPostHogFeatureFlags,
} from './telemetry/posthog/frontend';
import { IS_PROD } from './utils/env/is-prod';

/**
 * PostHog keys for production release gates. Production fails closed until a
 * fresh evaluation returns a strict `true`; staging always enables every gate.
 * `fileStorage` withholds Drive storage connect and File upload questions until
 * the production OAuth client, Drive scope verification and a live upload
 * check are complete.
 */
export const RELEASE_FLAG_KEYS = {
  calendarBookings: 'prod-release-calendar-bookings',
  campaignAutomation: 'prod-release-campaign-automation',
  fileStorage: 'prod-release-file-storage',
} as const;

/** A feature withheld in production until its release flag is on. */
export type ReleaseFeature = keyof typeof RELEASE_FLAG_KEYS;

/** Resolved availability of every release feature. */
type ReleaseFlagState = Record<ReleaseFeature, boolean>;

const DISABLED_FLAGS: ReleaseFlagState = {
  calendarBookings: false,
  campaignAutomation: false,
  fileStorage: false,
};

const STAGING_FLAGS: ReleaseFlagState = {
  calendarBookings: true,
  campaignAutomation: true,
  fileStorage: true,
};

const DEFAULT_FLAGS = IS_PROD ? DISABLED_FLAGS : STAGING_FLAGS;

const ReleaseFlagsContext = createContext<ReleaseFlagState>(DEFAULT_FLAGS);

/** Reads every production gate from a fresh PostHog evaluation, strict `true` only. */
function readFreshProductionFlags(): ReleaseFlagState {
  return {
    calendarBookings:
      isPostHogFeatureEnabled(RELEASE_FLAG_KEYS.calendarBookings, { fresh: true }) === true,
    campaignAutomation:
      isPostHogFeatureEnabled(RELEASE_FLAG_KEYS.campaignAutomation, { fresh: true }) === true,
    fileStorage:
      isPostHogFeatureEnabled(RELEASE_FLAG_KEYS.fileStorage, { fresh: true }) === true,
  };
}

/** Supplies release availability; production waits for a confirmed fresh evaluation. */
export function ReleaseFlagsProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = useState<ReleaseFlagState>(DEFAULT_FLAGS);
  // PostHog can publish an older in-flight response before it starts the
  // reload queued for a new identity or organization. Keep production hidden
  // until a follow-up request, issued from that callback, confirms the latest
  // SDK context.
  const confirmationStateRef = useRef<'needs-follow-up' | 'awaiting-follow-up' | 'ready'>(
    'needs-follow-up',
  );
  const issuingFollowUpReloadRef = useRef(false);

  useLayoutEffect(() => {
    if (!IS_PROD) return undefined;

    const unsubscribeReloading = onPostHogFeatureFlagsReloading(() => {
      if (issuingFollowUpReloadRef.current) return;

      confirmationStateRef.current = 'needs-follow-up';
      setFlags(DISABLED_FLAGS);
    });
    const unsubscribeFlags = onPostHogFeatureFlags((context) => {
      if (confirmationStateRef.current === 'needs-follow-up') {
        confirmationStateRef.current = 'awaiting-follow-up';
        setFlags(DISABLED_FLAGS);
        issuingFollowUpReloadRef.current = true;
        try {
          reloadPostHogFeatureFlags();
        } finally {
          issuingFollowUpReloadRef.current = false;
        }
        return;
      }

      confirmationStateRef.current = 'ready';
      setFlags(context?.errorsLoading ? DISABLED_FLAGS : readFreshProductionFlags());
    });

    reloadPostHogFeatureFlags();

    return () => {
      unsubscribeFlags();
      unsubscribeReloading();
    };
  }, []);

  return (
    <ReleaseFlagsContext.Provider value={flags}>{children}</ReleaseFlagsContext.Provider>
  );
}

/** True when the feature is released for the current identity and organization. */
export function useReleaseFeature(feature: ReleaseFeature): boolean {
  return useContext(ReleaseFlagsContext)[feature];
}
